import React, { useState } from 'react';
import {
  Search,
  Plus,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  Users,
  MapPin,
  Video,
  Calendar,
  DollarSign,
  Edit,
  Trash2,
  Mail,
  Copy,
  FileText,
} from 'lucide-react';

type ViewMode = 'courses' | 'students';

interface EnrolledStudent {
  id: string;
  name: string;
  attendance: number;
  paymentStatus: 'paid' | 'pending' | 'overdue';
  missedMeetings: number;
}

interface EnrollmentCourse {
  id: string;
  name: string;
  cohort: string;
  startDate: string;
  location: 'online' | 'hybrid' | 'in-person';
  isBundle: boolean;
  enrolledCount: number;
  totalSpaces: number;
  status: 'active' | 'upcoming' | 'completed';
  students: EnrolledStudent[];
}

interface StudentEnrollment {
  id: string;
  name: string;
  avatar?: string;
  plans: {
    planName: string;
    coursesCount: number;
    amount: number;
    status: 'paid' | 'pending' | 'overdue';
    dueDate: string;
    discount: number;
  }[];
  courses: {
    courseId: string;
    courseName: string;
    attendance: number;
    isActive: boolean;
    paymentStatus: 'paid' | 'pending';
  }[];
}

const mockCourses: EnrollmentCourse[] = [
  {
    id: '1',
    name: 'Mathematics Advanced',
    cohort: 'Cohort A2',
    startDate: '2024-02-01',
    location: 'hybrid',
    isBundle: false,
    enrolledCount: 22,
    totalSpaces: 30,
    status: 'active',
    students: [
      { id: 's1', name: 'Sarah Cohen', attendance: 95, paymentStatus: 'paid', missedMeetings: 1 },
      { id: 's2', name: 'David Levi', attendance: 88, paymentStatus: 'paid', missedMeetings: 2 },
      { id: 's3', name: 'Maya Green', attendance: 72, paymentStatus: 'pending', missedMeetings: 4 },
    ],
  },
  {
    id: '2',
    name: 'Physics Bundle',
    cohort: 'Cohort B1',
    startDate: '2024-03-01',
    location: 'online',
    isBundle: true,
    enrolledCount: 18,
    totalSpaces: 25,
    status: 'upcoming',
    students: [],
  },
  {
    id: '3',
    name: 'English Literature',
    cohort: 'Cohort A1',
    startDate: '2024-01-15',
    location: 'in-person',
    isBundle: false,
    enrolledCount: 28,
    totalSpaces: 28,
    status: 'active',
    students: [
      { id: 's4', name: 'Yossi Brown', attendance: 100, paymentStatus: 'paid', missedMeetings: 0 },
    ],
  },
];

const mockStudents: StudentEnrollment[] = [
  {
    id: 's1',
    name: 'Sarah Cohen',
    plans: [
      { planName: 'Annual Package', coursesCount: 3, amount: 2745, status: 'paid', dueDate: '2024-02-15', discount: 200 },
    ],
    courses: [
      { courseId: '1', courseName: 'Mathematics Advanced', attendance: 95, isActive: true, paymentStatus: 'paid' },
      { courseId: '3', courseName: 'English Literature', attendance: 88, isActive: true, paymentStatus: 'paid' },
    ],
  },
  {
    id: 's2',
    name: 'David Levi',
    plans: [
      { planName: 'Monthly Subscription', coursesCount: 2, amount: 199, status: 'pending', dueDate: '2024-02-20', discount: 0 },
    ],
    courses: [
      { courseId: '1', courseName: 'Mathematics Advanced', attendance: 88, isActive: true, paymentStatus: 'pending' },
    ],
  },
  {
    id: 's3',
    name: 'Maya Green',
    plans: [
      { planName: 'Single Course', coursesCount: 1, amount: 450, status: 'overdue', dueDate: '2024-01-15', discount: 50 },
    ],
    courses: [
      { courseId: '1', courseName: 'Mathematics Advanced', attendance: 72, isActive: true, paymentStatus: 'pending' },
    ],
  },
];

const locationIcons: Record<EnrollmentCourse['location'], React.ReactNode> = {
  online: <Video className="w-4 h-4 text-blue-500" />,
  hybrid: <><Video className="w-3 h-3 text-blue-500" /><MapPin className="w-3 h-3 text-green-500" /></>,
  'in-person': <MapPin className="w-4 h-4 text-green-500" />,
};

const paymentStatusStyles: Record<string, string> = {
  paid: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  overdue: 'bg-red-100 text-red-700',
};

export function EnrollmentTab() {
  const [viewMode, setViewMode] = useState<ViewMode>('courses');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [contextMenuId, setContextMenuId] = useState<string | null>(null);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('courses')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'courses'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Courses
            </button>
            <button
              onClick={() => setViewMode('students')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === 'students'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Students
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={viewMode === 'courses' ? 'Search courses...' : 'Search students...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 w-64"
            />
          </div>
        </div>

        <button className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add New Lead
        </button>
      </div>

      {/* Content */}
      {viewMode === 'courses' ? (
        <CoursesView
          courses={mockCourses}
          searchQuery={searchQuery}
          expandedId={expandedId}
          onToggleExpand={(id) => setExpandedId(expandedId === id ? null : id)}
          contextMenuId={contextMenuId}
          onContextMenu={setContextMenuId}
        />
      ) : (
        <StudentsView
          students={mockStudents}
          searchQuery={searchQuery}
          expandedId={expandedId}
          onToggleExpand={(id) => setExpandedId(expandedId === id ? null : id)}
          contextMenuId={contextMenuId}
          onContextMenu={setContextMenuId}
        />
      )}
    </div>
  );
}

interface CoursesViewProps {
  courses: EnrollmentCourse[];
  searchQuery: string;
  expandedId: string | null;
  onToggleExpand: (id: string) => void;
  contextMenuId: string | null;
  onContextMenu: (id: string | null) => void;
}

function CoursesView({ courses, searchQuery, expandedId, onToggleExpand, contextMenuId, onContextMenu }: CoursesViewProps) {
  const filteredCourses = courses.filter(course =>
    course.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-3">
      {filteredCourses.map((course) => (
        <div
          key={course.id}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          {/* Course Header */}
          <div
            className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50"
            onClick={() => onToggleExpand(course.id)}
          >
            {/* Course Icon */}
            <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center text-lg font-bold text-teal-600">
              {course.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
            </div>

            {/* Course Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-gray-900">{course.name}</h3>
                {course.isBundle && (
                  <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                    Bundle
                  </span>
                )}
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  course.status === 'active' ? 'bg-green-100 text-green-700' :
                  course.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {course.status.charAt(0).toUpperCase() + course.status.slice(1)}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                <span>{course.cohort}</span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(course.startDate).toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1">
                  {locationIcons[course.location]}
                  {course.location.charAt(0).toUpperCase() + course.location.slice(1)}
                </span>
              </div>
            </div>

            {/* Enrollment Count */}
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium">
                {course.enrolledCount}/{course.totalSpaces}
              </span>
              <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-teal-500 rounded-full"
                  style={{ width: `${(course.enrolledCount / course.totalSpaces) * 100}%` }}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="px-3 py-1.5 text-sm text-teal-600 hover:bg-teal-50 rounded-lg"
              >
                See Students
              </button>
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onContextMenu(contextMenuId === course.id ? null : course.id);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>

                {contextMenuId === course.id && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <Edit className="w-4 h-4" />
                      Edit Course
                    </button>
                    <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <Copy className="w-4 h-4" />
                      Duplicate Course
                    </button>
                    <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <Mail className="w-4 h-4" />
                      Send Mail to All
                    </button>
                    <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <Users className="w-4 h-4" />
                      Add Students
                    </button>
                    <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <DollarSign className="w-4 h-4" />
                      Finance Page
                    </button>
                    <hr className="my-1" />
                    <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                      Delete Course
                    </button>
                  </div>
                )}
              </div>
              {expandedId === course.id ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {/* Expanded Students */}
          {expandedId === course.id && (
            <div className="border-t border-gray-200 bg-gray-50 p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Enrolled Students</h4>
              {course.students.length > 0 ? (
                <div className="space-y-2">
                  {course.students.map((student) => (
                    <div
                      key={student.id}
                      className="flex items-center gap-4 p-3 bg-white rounded-lg border border-gray-200"
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                        {student.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="flex-1 font-medium text-gray-900">{student.name}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              student.attendance >= 90 ? 'bg-green-500' :
                              student.attendance >= 75 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${student.attendance}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600 w-10">{student.attendance}%</span>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${paymentStatusStyles[student.paymentStatus]}`}>
                        {student.paymentStatus.charAt(0).toUpperCase() + student.paymentStatus.slice(1)}
                      </span>
                      {student.missedMeetings > 0 && (
                        <span className="text-xs text-red-600">{student.missedMeetings} missed</span>
                      )}
                      <button className="text-sm text-teal-600 hover:text-teal-700">
                        Manage
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No students enrolled yet</p>
              )}
            </div>
          )}
        </div>
      ))}

      {filteredCourses.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No courses found</p>
        </div>
      )}
    </div>
  );
}

interface StudentsViewProps {
  students: StudentEnrollment[];
  searchQuery: string;
  expandedId: string | null;
  onToggleExpand: (id: string) => void;
  contextMenuId: string | null;
  onContextMenu: (id: string | null) => void;
}

function StudentsView({ students, searchQuery, expandedId, onToggleExpand, contextMenuId, onContextMenu }: StudentsViewProps) {
  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-3">
      {filteredStudents.map((student) => (
        <div
          key={student.id}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          {/* Student Header */}
          <div
            className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50"
            onClick={() => onToggleExpand(student.id)}
          >
            {/* Avatar */}
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-sm font-medium text-teal-700">
              {student.name.split(' ').map(n => n[0]).join('')}
            </div>

            {/* Student Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900">{student.name}</h3>
              <p className="text-sm text-gray-500">{student.plans.length} plan(s) | {student.courses.length} course(s)</p>
            </div>

            {/* Plans Summary */}
            <div className="hidden md:flex flex-col gap-1">
              {student.plans.map((plan, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">{plan.planName}</span>
                  <span className="font-medium">${plan.amount}</span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${paymentStatusStyles[plan.status]}`}>
                    {plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
                  </span>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button className="px-3 py-1.5 text-sm text-teal-600 hover:bg-teal-50 rounded-lg">
                Manage Courses
              </button>
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onContextMenu(contextMenuId === student.id ? null : student.id);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>

                {contextMenuId === student.id && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <Edit className="w-4 h-4" />
                      Edit Profile
                    </button>
                    <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <FileText className="w-4 h-4" />
                      Manage Courses
                    </button>
                    <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <Mail className="w-4 h-4" />
                      Send Mail
                    </button>
                    <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <DollarSign className="w-4 h-4" />
                      See Finance
                    </button>
                    <hr className="my-1" />
                    <button className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                      Delete Student
                    </button>
                  </div>
                )}
              </div>
              {expandedId === student.id ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {/* Expanded Courses */}
          {expandedId === student.id && (
            <div className="border-t border-gray-200 bg-gray-50 p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Enrolled Courses</h4>
              <div className="space-y-2">
                {student.courses.map((course) => (
                  <div
                    key={course.courseId}
                    className="flex items-center gap-4 p-3 bg-white rounded-lg border border-gray-200"
                  >
                    <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center text-xs font-bold text-teal-600">
                      {course.courseName.split(' ').map(w => w[0]).join('').slice(0, 2)}
                    </div>
                    <span className="flex-1 font-medium text-gray-900">{course.courseName}</span>
                    <div className="flex items-center gap-2">
                      <div className="relative w-8 h-8">
                        <svg className="w-8 h-8 transform -rotate-90">
                          <circle cx="16" cy="16" r="14" stroke="#e5e7eb" strokeWidth="3" fill="none" />
                          <circle
                            cx="16" cy="16" r="14"
                            stroke={course.attendance >= 90 ? '#22c55e' : course.attendance >= 75 ? '#eab308' : '#ef4444'}
                            strokeWidth="3"
                            fill="none"
                            strokeDasharray={`${course.attendance * 0.88} 88`}
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                      <span className="text-sm text-gray-600">{course.attendance}%</span>
                    </div>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${course.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {course.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${paymentStatusStyles[course.paymentStatus]}`}>
                      {course.paymentStatus.charAt(0).toUpperCase() + course.paymentStatus.slice(1)}
                    </span>
                    <button className="text-sm text-teal-600 hover:text-teal-700">
                      Profile
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}

      {filteredStudents.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No students found</p>
        </div>
      )}
    </div>
  );
}
